// pages/verify/token.js (VerifyPage)
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  findClientByVerificationToken,
  markClientVerified,
} from "../../helpers/client";
import { useAuth } from "../../AuthContext";
import Navbar from "../../components/Navbar";
import Phonebar from "../../components/Phonebar";

import { db } from "../../firebase";
import { doc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore"; // ✅ getDoc added
import { sendTextMessage } from "../../Helpers";
import { getStaffSMSList } from "../../helpers/client"; // ✅ added
import { getLocationById } from "../../helpers/location"; // ✅ optional but used for nicer staff SMS

const VerifyPage = () => {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);
  const [client, setClient] = useState(null);
  const [language, setLanguage] = useState("English");

  // Fallback names by numeric id; always coerce lookup with Number()
  const idsToAddresses = {
    0: "JMJ Orlando Clinic",
    1: "JMJ Kissimmee Clinic",
  };

  const formatHour = (hour) => {
    const h = parseInt(hour, 10);
    if (Number.isNaN(h)) return "";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display}:00 ${h >= 12 ? "PM" : "AM"}`;
  };

  // ✅ same staff-facing group notification logic as AppointmentCard
  const sendStaffFacingNotification = async (message) => {
    try {
      // Read which group is active
      const groupDoc = await getDoc(doc(db, "settings", "SMS_GROUP"));
      const group = groupDoc.exists()
        ? (groupDoc.data()?.group || "").toString().toLowerCase()
        : "staff";

      let recipients = [];

      if (group === "staff") {
        const staffUsers = await getStaffSMSList();
        recipients = staffUsers
          .map((s) => s.phone?.toString())
          .filter((p) => p);
      } else if (group === "development") {
        const devDoc = await getDoc(doc(db, "settings", "SMS_DEVELOPMENT"));
        const numbers = devDoc.exists() ? devDoc.data()?.numbers || [] : [];
        recipients = Array.isArray(numbers) ? numbers.map((n) => n.toString()) : [];
      } else {
        console.warn(
          `Unknown SMS group "${group}", defaulting to no recipients except admin.`
        );
        recipients = [];
      }

      // Deduplicate
      recipients = Array.from(new Set(recipients.filter(Boolean)));

      const sendPromises = [];

      // Send to group recipients
      recipients.forEach((number) => {
        sendPromises.push(
          sendTextMessage({
            message,
            yourPhoneNumber: "4073059084",
            recipientPhoneNumber: number,
            senderName: "JMJPC",
          })
        );
      });

      // Also send to the same number (trigger email notification) in staff mode
      if (group === "staff") {
        sendPromises.push(
          sendTextMessage({
            message,
            yourPhoneNumber: "4073059084",
            recipientPhoneNumber: "4073059084",
            senderName: "JMJPC",
          })
        );
      }

      await Promise.all(sendPromises);
    } catch (err) {
      console.error("Error sending staff-facing notification:", err);
    }
  };

  // Helper to get localized strings
  const getText = (key, lang) => {
    const texts = {
      title: { English: "Account Verification", Spanish: "Verificación de cuenta" },
      verifying: { English: "Verifying...", Spanish: "Verificando..." },
      invalid: {
        English: "Invalid or expired verification link.",
        Spanish: "Enlace de verificación inválido o expirado.",
      },
      already: {
        English: "Your account is already verified. Redirecting...",
        Spanish: "Su cuenta ya está verificada. Redirigiendo...",
      },
      success: {
        English: "Your account has been verified. Redirecting...",
        Spanish: "Su cuenta ha sido verificada. Redirigiendo...",
      },
      error: {
        English: "An error occurred during verification. Please try again later.",
        Spanish:
          "Ocurrió un error durante la verificación. Por favor inténtelo de nuevo más tarde.",
      },
      backToLogin: { English: "Back to Login", Spanish: "Volver al inicio de sesión" },
    };
    return texts[key][lang] || texts[key]["English"];
  };

  useEffect(() => {
    setStatus(getText("verifying", language));
    if (!token) return;

    const runVerification = async () => {
      try {
        // ========= DEBUG LOGS (VERIFY) =========
        console.log("========== [VERIFY DEBUG][VERIFY PAGE] ==========");
        console.log("[VERIFY] window.location.href:", window.location.href);
        console.log("[VERIFY] window.location.hash:", window.location.hash);
        console.log("[VERIFY] react-router location.pathname:", location.pathname);
        console.log("[VERIFY] react-router location.search:", location.search);
        console.log("[VERIFY] token from useParams():", token);
        console.log("[VERIFY] token length:", token?.length);
        console.log("[VERIFY] token looks hex:", /^[0-9a-f]+$/i.test(token));
        console.log("[VERIFY] token contains '/':", token?.includes("/"));
        // ======================================

        const clientResult = await findClientByVerificationToken(token);

        // ========= DEBUG LOGS (VERIFY) =========
        console.log("[VERIFY] findClientByVerificationToken result:", clientResult);
        if (clientResult) {
          console.log("[VERIFY] matched client id:", clientResult.id);
          console.log(
            "[VERIFY] matched client stored verificationToken:",
            clientResult.verificationToken
          );
          console.log(
            "[VERIFY] stored token === url token:",
            clientResult.verificationToken === token
          );
          console.log("[VERIFY] matched client verified:", clientResult.verified);
          console.log("[VERIFY] matched client language:", clientResult.language);
          console.log("[VERIFY] matched pendingAppointmentId:", clientResult.pendingAppointmentId);
        }
        // ======================================

        if (!clientResult) {
          setStatus(getText("invalid", language));
          return;
        }

        // determine language from client immediately
        const lang = clientResult.language === "Spanish" ? "Spanish" : "English";
        setLanguage(lang);
        setClient(clientResult);

        // If already verified, just send them to /home
        if (clientResult.verified) {
          setStatus(getText("already", lang));
          setDone(true);

          login(clientResult.id, true);
          navigate("/home", { replace: true });
          return;
        }

        await markClientVerified(clientResult.id);

        console.log("[VERIFY] markClientVerified() completed for client:", clientResult.id);

        // Confirm the appointment that was booked during signup (if present)
        let confirmedApptData = null;

        try {
          if (clientResult.pendingAppointmentId) {
            const apptRef = doc(db, "appointments", clientResult.pendingAppointmentId);

            // ✅ read appointment before updating, so we can notify staff with details
            const apptSnap = await getDoc(apptRef);
            confirmedApptData = apptSnap.exists() ? apptSnap.data() : null;

            await updateDoc(apptRef, {
              confirmed: true,
              confirmedAt: serverTimestamp(),
            });

            // Clear pointer on client doc
            await updateDoc(doc(db, "clients", clientResult.id), {
              pendingAppointmentId: "",
            });
          }
        } catch (e) {
          console.error("[VERIFY] failed to confirm appointment:", e);
        }

        // ✅ Staff-facing notification after confirmation
        try {
          if (clientResult.pendingAppointmentId) {
            const first = clientResult.firstName || "Client";
            const last = clientResult.lastName || "";
            const phoneDigits = clientResult.id; // client doc id is phone digits in your system

            let dateStr = "";
            let timeStr = "";
            let locName = "Clinic";

            if (confirmedApptData) {
              dateStr = confirmedApptData.date || "";
              timeStr = formatHour(confirmedApptData.startTime);

              const locId = Number(confirmedApptData.location);
              locName = idsToAddresses[locId] || locName;

              // best-effort: replace fallback with actual location name
              try {
                if (!Number.isNaN(locId)) {
                  const loc = await getLocationById(locId);
                  locName = loc?.name ?? locName;
                }
              } catch (e) {
                console.warn("[VERIFY] getLocationById failed; using fallback", e);
              }
            }

            const detailPart =
              dateStr && timeStr
                ? ` for ${timeStr} on ${dateStr} at ${locName}`
                : "";

            await sendStaffFacingNotification(
              `JMJPC BOOKING: ${first} ${last} (${phoneDigits}) CONFIRMED their appointment${detailPart}.`
            );
          }
        } catch (e) {
          console.error("[VERIFY] failed sending staff-facing confirmation SMS:", e);
        }

        // Send final confirmation text with /home link
        try {
          const origin = window.location.origin;
          const homeLink = `${origin}/#/home`;

          const finalMsg =
            lang === "Spanish"
              ? `JMJPC CITA: Su cita está confirmada. Que tenga un buen día. Puede reprogramar o cancelar haciendo clic en el enlace a continuación: ${homeLink}`
              : `JMJPC BOOKING: Your appointment is confirmed. Have a great day. You can reschedule or cancel by clicking the link below: ${homeLink}`;

          await sendTextMessage({
            message: finalMsg,
            yourPhoneNumber: "4073059084",
            recipientPhoneNumber: clientResult.id, // phone digits
            senderName: "JMJPC",
          });
        } catch (e) {
          console.error("[VERIFY] failed sending final confirmation SMS:", e);
        }

        setStatus(getText("success", lang));
        setDone(true);

        // Auto-login and redirect to /home
        login(clientResult.id, true);
        navigate("/home", { replace: true });
      } catch (err) {
        console.error("Verification error:", err);
        setStatus(
          err?.message
            ? `${getText("error", language)} (${err.message})`
            : getText("error", language)
        );
      }
    };

    runVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/StandingDoctor.webp')" }}
    >
      <Navbar includeLogout={false} language={language} />
      <Phonebar />
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="max-w-md w-full bg-white p-8 rounded shadow">
          <h1 className="text-2xl font-bold mb-4 text-center">{getText("title", language)}</h1>
          <p className="mb-6 text-center">{status}</p>

          {!done && (
            <div className="flex justify-center">
              <button
                onClick={() => navigate("/login")}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
              >
                {getText("backToLogin", language)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
